import type { CSSProperties, FC, ReactNode } from 'react'
import type { ProcessStage } from '@/constants/process'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { StageDrawing } from '@/components/process/stage-drawing'

import { PROCESS_STAGES } from '@/constants/process'

const delay = (value: number): CSSProperties => ({ '--delay': `${value}ms` } as CSSProperties)

interface StageRowProps {
    stage: ProcessStage
    reached: boolean
    current: boolean
    registerRef: (element: HTMLLIElement | null) => void
    index: number
}

const StageRow: FC<StageRowProps> = ({ stage, reached, current, registerRef, index }): ReactNode => {
    return (
        <li
            ref={registerRef}
            data-stage={index}
            data-reveal={reached ? 'in' : 'pre'}
            aria-current={current ? 'step' : undefined}
            className='relative grid gap-8 py-14 pl-12 pr-gutter desk:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] desk:gap-16 desk:py-20 desk:pl-28'
        >
            <span
                className={cn(
                    'absolute left-[calc(var(--gutter)-5px)] top-[4.6rem] size-[11px] border transition-colors duration-500 desk:left-[calc(var(--gutter)+19px)] desk:top-[6.1rem]',
                    reached ? 'border-signal bg-signal' : 'border-mute bg-background'
                )}
                aria-hidden='true'
            />

            <div>
                <p className='hud reveal-fade flex items-center gap-3'>
                    <span className={cn('transition-colors duration-500', current ? 'text-signal' : 'text-mute')}>
                        {stage.number}
                    </span>

                    <span className='h-px w-8 bg-hairline' aria-hidden='true' />

                    <span className='text-ink'>
                        {stage.duration}
                    </span>
                </p>

                <h3 className='mt-5 font-heading text-[clamp(30px,3.6vw,52px)] font-medium uppercase leading-[1] tracking-[-0.02em]'>
                    <span className='reveal-line'>
                        <span className='reveal-inner text-ink' style={delay(60)}>
                            {stage.title}
                        </span>
                    </span>
                </h3>

                <p className='reveal-fade mt-6 max-w-[52ch] text-[15px] leading-[1.65] text-ink/85' style={delay(160)}>
                    {stage.summary}
                </p>

                <dl className='reveal-fade mt-8 max-w-[600px] border-t border-hairline' style={delay(240)}>
                    {[
                        { label: 'Duration', value: stage.duration },
                        { label: 'Scanned or measured', value: stage.measured },
                        { label: 'Deliverables', value: stage.deliverables },
                        { label: 'Who is involved', value: stage.people }
                    ].map(row => (
                        <div key={row.label} className='grid gap-1 border-b border-hairline py-3 sm:grid-cols-[11rem_minmax(0,1fr)] sm:gap-6'>
                            <dt className='hud text-mute'>
                                {row.label}
                            </dt>

                            <dd className='text-[14px] leading-[1.55] text-ink'>
                                {row.value}
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>

            <div className='relative aspect-[8/7] w-full max-w-[460px] border border-hairline desk:justify-self-end'>
                <span className='hud absolute left-3 top-3 text-mute' aria-hidden='true'>
                    Fig. 02.{index + 1}
                </span>

                <div className='absolute inset-0 p-6'>
                    <StageDrawing kind={stage.drawing} label={stage.drawingLabel} />
                </div>
            </div>
        </li>
    )
}

/**
 * Six stages on a vertical line that fills as the visitor scrolls. Each stage marks itself reached
 * as it crosses the middle of the screen, which draws in its line drawing.
 */
export const ProcessTimeline: FC = (): ReactNode => {
    const listRef = useRef<HTMLOListElement>(null)
    const fillRef = useRef<HTMLSpanElement>(null)
    const stageRefs = useRef<Array<HTMLLIElement | null>>([])
    const [reached, setReached] = useState(-1)

    useEffect(() => {
        const list = listRef.current

        if (!list) return

        let raf = 0

        const update = (): void => {
            const rect = list.getBoundingClientRect()
            const middle = window.innerHeight * 0.55
            const progress = Math.min(1, Math.max(0, (middle - rect.top) / Math.max(1, rect.height)))

            if (fillRef.current) fillRef.current.style.transform = `scaleY(${progress.toFixed(4)})`

            let last = -1

            stageRefs.current.forEach((stage, index) => {
                if (stage && stage.getBoundingClientRect().top + 120 < middle) last = index
            })

            setReached(previous => (previous === last ? previous : last))
        }

        const onScroll = (): void => {
            cancelAnimationFrame(raf)
            raf = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onScroll)

        return () => {
            cancelAnimationFrame(raf)
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)
        }
    }, [])

    return (
        <ol ref={listRef} className='relative border-t border-hairline'>
            <span className='absolute bottom-0 left-gutter top-0 w-px bg-hairline desk:left-[calc(var(--gutter)+24px)]' aria-hidden='true'>
                <span ref={fillRef} className='absolute inset-0 origin-top bg-signal' style={{ transform: 'scaleY(0)' }} />
            </span>

            {PROCESS_STAGES.map((stage, index) => (
                <StageRow
                    key={stage.id}
                    stage={stage}
                    index={index}
                    reached={index <= reached}
                    current={index === reached}
                    registerRef={element => {
                        stageRefs.current[index] = element
                    }}
                />
            ))}
        </ol>
    )
}
